import React, { useState } from 'react';
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Text,
  Box,
  Spinner,
  Alert,
  Collapsible,
  CollapsibleBody,
  CollapsibleLink,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { Salary, TDS_FILING_DATA_QUERY_KEYS } from 'api/tdsFilingData';
import { paiseToRupees, formatEpochDate } from './utils';

interface SalaryTableProps {
  filingId: string;
}

const SalaryExpandedRow = ({ salary }: { salary: Salary }) => (
  <Box padding="spacing.4" backgroundColor="surface.background.gray.subtle">
    {salary.s16.length > 0 && (
      <Box marginBottom="spacing.4">
        <Text weight="semibold" marginBottom="spacing.2">
          Section 16 Deductions
        </Text>
        {salary.s16.map((s) => (
          <Box
            key={s.salary_id}
            display="flex"
            gap="spacing.6"
            paddingY="spacing.1"
          >
            <Text size="small">#{s.serial_no}</Text>
            <Text size="small">{s.section_code}</Text>
            <Text size="small">{paiseToRupees(s.amount)}</Text>
          </Box>
        ))}
      </Box>
    )}
    {salary.c6a.length > 0 && (
      <Box>
        <Text weight="semibold" marginBottom="spacing.2">
          Chapter VI-A Deductions
        </Text>
        {salary.c6a.map((c) => (
          <Box
            key={c.c6a_id}
            display="flex"
            gap="spacing.6"
            paddingY="spacing.1"
          >
            <Text size="small">#{c.serial_no}</Text>
            <Text size="small">{c.section_id}</Text>
            <Text size="small">Gross: {paiseToRupees(c.gross_amount)}</Text>
            <Text size="small">Qualifying: {paiseToRupees(c.qualifying_amount)}</Text>
            <Text size="small">Deductible: {paiseToRupees(c.deductible_amount)}</Text>
          </Box>
        ))}
      </Box>
    )}
    {salary.s16.length === 0 && salary.c6a.length === 0 && (
      <Text size="small" color="surface.text.gray.muted">
        No Section 16 or Chapter VI-A deductions
      </Text>
    )}
  </Box>
);

export const SalaryTable: React.FC<SalaryTableProps> = ({ filingId }) => {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, isError } = useQuery({
    queryKey: [...TDS_FILING_DATA_QUERY_KEYS.salaries(filingId), page],
    queryFn: () => api.tdsFilingData.getSalaries(filingId, { page, page_size: pageSize }),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding="spacing.6">
        <Spinner accessibilityLabel="Loading salaries" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert color="negative" description="Failed to load salary records. Please try again." />
    );
  }

  const salaries = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  return (
    <Table
      data={{ nodes: salaries.map((s) => ({ ...s, id: s.salary_id })) }}
      isLoading={isLoading}
      pagination={
        <TablePagination
          currentPage={page - 1}
          defaultPageSize={pageSize}
          paginationType="server"
          totalItemCount={totalCount}
          onPageChange={({ page: p }) => setPage(p + 1)}
        />
      }
    >
      {(tableNodes) => (
        <>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>PAN</TableHeaderCell>
              <TableHeaderCell>Employee Name</TableHeaderCell>
              <TableHeaderCell>Category</TableHeaderCell>
              <TableHeaderCell>Employment From</TableHeaderCell>
              <TableHeaderCell>Employment To</TableHeaderCell>
              <TableHeaderCell>Total Salary</TableHeaderCell>
              <TableHeaderCell>Income Tax</TableHeaderCell>
              <TableHeaderCell>Net Tax Payable</TableHeaderCell>
              <TableHeaderCell>Tax Deducted</TableHeaderCell>
              <TableHeaderCell>Tax Shortfall</TableHeaderCell>
              <TableHeaderCell>Regime</TableHeaderCell>
              <TableHeaderCell>Details</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {tableNodes.map((salary) => (
              <TableRow key={salary.salary_id} item={salary}>
                <TableCell>
                  <Text>{salary.deductee_pan}</Text>
                </TableCell>
                <TableCell>
                  <Text>{salary.employee_name}</Text>
                </TableCell>
                <TableCell>
                  <Text>{salary.employee_category ?? '—'}</Text>
                </TableCell>
                <TableCell>
                  <Text>{formatEpochDate(salary.employment_from)}</Text>
                </TableCell>
                <TableCell>
                  <Text>{formatEpochDate(salary.employment_to)}</Text>
                </TableCell>
                <TableCell>
                  <Text>{paiseToRupees(salary.total_salary)}</Text>
                </TableCell>
                <TableCell>
                  <Text>{paiseToRupees(salary.income_tax)}</Text>
                </TableCell>
                <TableCell>
                  <Text>{paiseToRupees(salary.net_tax_payable)}</Text>
                </TableCell>
                <TableCell>
                  <Text>{paiseToRupees(salary.total_tax_deducted)}</Text>
                </TableCell>
                <TableCell>
                  <Text
                    color={
                      (salary.tax_shortfall ?? 0) > 0
                        ? 'feedback.text.negative.intense'
                        : 'surface.text.gray.normal'
                    }
                  >
                    {paiseToRupees(salary.tax_shortfall)}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text>{salary.old_regime_indicator === 'O' ? 'Old' : salary.old_regime_indicator === 'N' ? 'New' : '—'}</Text>
                </TableCell>
                <TableCell>
                  <Collapsible>
                    <CollapsibleLink>
                      S16/C6A ({(salary.section16_count ?? 0) + (salary.chapter6a_count ?? 0)})
                    </CollapsibleLink>
                    <CollapsibleBody>
                      <SalaryExpandedRow salary={salary as Salary} />
                    </CollapsibleBody>
                  </Collapsible>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </>
      )}
    </Table>
  );
};
